<template>
    <section class="grid-articles">
        <h2 v-if="title">
            {{ title }}
        </h2>
        <div class="grid-articles--filter" v-if="categories.length > 0">
            <ui-model-provider v-model="filterValue" v-slot="{ value, updateValue }">
                <ui-select
                        :disabled="false"
                        :required="true"
                        :value="value"
                        name="category"
                        id="id_category"
                        @change="updateValue">
                    <template slot="label">
                        {{ 'Artikel filtern' }}
                    </template>
                    <template slot="default">
                        <ui-option :value="''" :disabled="true">
                            {{ 'Kategorie wählen' }}
                        </ui-option>
                        <ui-option :value="''" :disabled="false">
                            {{ 'Alle Kategorien' }}
                        </ui-option>
                        <ui-option
                                :value="category.slug"
                                v-for="category in categories"
                                :key="category.slug">
                            {{ category.name }}
                        </ui-option>
                    </template>
                </ui-select>
            </ui-model-provider>
        </div>
        <ul class="grid-articles--list u-reset">
            <slot/>
            <li class="grid-articles--tile" v-for="(article) in articles" :key="article.path">
                <a class="grid-articles--tile-link u-reset" :href="getArticleLink(article.path)">
                    <figure class="grid-articles--tile-figure" v-if="article.image && article.image.urls">
                        <img :src="getImageSrc(article)" :alt="getImageAlt(article)" class="grid-articles--tile-image">
                    </figure>
                    <div :class="article.image && article.image.urls ? 'grid-articles--tile-container' : 'grid-articles--tile-container-without-image'">
                        <div>
                            <h3 class="grid-articles--tile-title grid-articles--dark-text" itemprop="headline">
                                {{ article.title }}
                            </h3>
                            <h4 class="grid-articles--tile-subtitle" itemprop="description">
                                {{ article.lead_list }}
                            </h4>
                            <div :class="article.image && article.image.urls ? 'grid-articles--tile-article-info-container' : 'grid-articles--tile-article-info-container-with-margin'">
                                <p class="grid-articles--tile-article-info" itemprop="author">
                                    {{ article.author }}
                                </p>
                                <p class="grid-articles--tile-article-info" itemprop="datePublished">
                                    {{ article.publication_date }}
                                </p>
                            </div>
                        </div>
                        <div>
                            <div class="grid-articles--tile-footer">
                                <p class="grid-articles--tile-tag" itemprop="keywords" v-if="article.category">
                                    {{ article.category }}
                                </p>
                                <div class="grid-articles--tile-arrow-wrapper grid-articles--dark-text">
                                    <div class="grid-articles--tile-arrow"/>
                                </div>
                            </div>
                        </div>
                    </div>
                </a>
            </li>
        </ul>
        <button class="grid-articles--button-load" v-if="paginator.has_next" @click="loadMore">
            {{ paginator.load_more_label }}
        </button>
    </section>
</template>

<script>
    import Axios from '../../../util/service';
    import UiOption from '@dreipol/vue-ui/src/components/form/option/option.vue';
    import UiSelect from '@dreipol/vue-ui/src/components/form/select/select.vue';
    import UiModelProvider from '@dreipol/vue-ui/src/components/form/model-provider/model-provider.vue';
    import { nextTick } from 'vue';

    export default {
        components: { UiModelProvider, UiSelect, UiOption },
        props: {
            title: {
                type: String,
                required: false,
                default: '',
            },
            articles: {
                type: Array,
                required: true,
                default: () => [],
            },
            paginator: {
                type: Object,
                required: true,
                default: () => ({}),
            },
            categories: {
                type: Array,
                required: false,
                default: () => [],
            },
        },
        data() {
            return {
                filterValue: '',
                currentPage: 1,
                initialized: false,
            };
        },
        methods: {
            getImageSrc(article) {
                if (article.image && article.image.urls && article.image.urls.default) {
                    return article.image.urls.default[0];
                }
                return '';
            },
            getImageAlt(article) {
                if (article.image && article.image.alt) {
                    return article.image.alt;
                }
                return '';
            },
            loadMore() {
                this.currentPage++;
                const url = new URL(window.location);
                url.searchParams.set('page', this.currentPage);
                window.history.replaceState({}, '', url);

                Axios.get(this.paginator.load_more, {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    data: null, // See: https://github.com/axios/axios/issues/86#issuecomment-389298860
                }).then((res) => {
                    this.articles = res.data.articles;
                    this.paginator = res.data.paginator;
                });
            },
            fetchFilteredArticles(filterSlug) {
                const url = new URL(window.location);
                url.searchParams.delete('page');
                this.currentPage = 1;
                if (filterSlug) {
                    url.searchParams.set('filter_id', `category=${ filterSlug }`);
                } else {
                    url.searchParams.delete('filter_id');
                }

                window.history.replaceState({}, '', url);

                const params = {};
                if (filterSlug) {
                    params.filter_id = `category=${ filterSlug }`;
                    params.page = this.currentPage;
                }

                Axios.get(window.location.pathname, {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    params: params,
                    data: null,
                }).then((res) => {
                    this.articles = res.data.articles;
                    this.paginator = res.data.paginator;
                })
                    .catch(() => {
                        console.error('Failed to fetch filtered articles');
                    });
            },
            getArticleLink(articlePath) {
                const filterSlug = this.filterValue;
                const url = new URL(articlePath, window.location.origin);
                if (filterSlug) {
                    url.searchParams.set('filter_id', `category=${ filterSlug }`);
                }
                if (this.currentPage > 1) {
                    url.searchParams.set('page', this.currentPage);
                }
                return url.pathname + url.search;
            },
        },
        watch: {
            filterValue(newValue) {
                if (!this.initialized) {
                    return;
                }
                this.fetchFilteredArticles(newValue);
            },
        },
        mounted() {
            const urlParams = new URLSearchParams(window.location.search);
            const filterParam = urlParams.get('filter_id');
            if (filterParam && filterParam.startsWith('category=')) {
                this.filterValue = filterParam.replace('category=', '');
            }
            this.currentPage = this.paginator.current_page;

            nextTick(() => {
                this.initialized = true;
            });
        },
    };
</script>
